import React from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useSubmitPartnerEarn } from '~/hooks/apis/loyalty/useSubmitPartnerEarn';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { CppPartner } from '~/types/costPerPoint';
import type { PartnerEarnBulkResult, PartnerEarnCsvRow } from '~/types/loyaltyContract';

import { formatDecimal } from '~/utils/numberFormatters';

import { partnerLimitsLabel } from '../partnerEarnCredit';

interface Props {
  rows: Array<PartnerEarnCsvRow>;
  partner: CppPartner;
  onBack: () => void;
  onSubmitSuccess: (response: PartnerEarnBulkResult) => void;
}

const MAX_PREVIEW_ROWS = 5;

export default function ReviewStep({ rows, partner, onBack, onSubmitSuccess }: Props) {
  const { user } = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();

  const { mutate, isPending, progress } = useSubmitPartnerEarn({
    onSuccess: (data) => {
      const msg =
        data.failedCount > 0
          ? `Partner earn processed: ${data.successCount} succeeded, ${data.failedCount} failed`
          : 'Partner earn credits queued successfully';
      enqueueSnackbar(msg, { variant: data.failedCount > 0 ? 'warning' : 'success' });
      onSubmitSuccess(data);
    },
    onError: (error) => {
      enqueueSnackbar(`Submission failed: ${error.message}`, { variant: 'error' });
    },
  });

  const previewRows = rows.slice(0, MAX_PREVIEW_ROWS);
  const progressPercent = progress.total > 0 ? (progress.processed / progress.total) * 100 : 0;

  const handleSubmit = () => {
    mutate({ partnerId: partner.id, rows });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Confirm partner earn
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Credits are queued asynchronously and attributed to {partner.name}.
      </Typography>

      <Stack direction="row" spacing={3} mb={3}>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            INITIATED BY
          </Typography>
          <Typography variant="body2">{user?.fullName || 'Unknown'}</Typography>
        </Box>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            TIMESTAMP
          </Typography>
          <Typography variant="body2">{new Date().toLocaleString()}</Typography>
        </Box>
      </Stack>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Partner
        </Typography>
        <Typography variant="body2">{partner.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {partner.partnerType} · {partnerLimitsLabel(partner)}
        </Typography>
      </Box>

      <Box
        sx={{
          border: 1,
          borderColor: 'primary.main',
          borderRadius: 1,
          p: 2,
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="subtitle1">Total members</Typography>
          <Typography variant="body2" color="text.secondary">
            Rows that will be queued as this partner
          </Typography>
        </Box>
        <Typography variant="h3" color="primary">
          {formatDecimal(rows.length)}
        </Typography>
      </Box>

      <Typography variant="subtitle1" gutterBottom>
        Member list
        <Typography component="span" variant="body2" color="text.secondary" ml={2}>
          Showing top {previewRows.length} of {rows.length}
        </Typography>
      </Typography>

      <TableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Account number</TableCell>
              <TableCell>Base points</TableCell>
              <TableCell>Status credits</TableCell>
              <TableCell>Activity title</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {previewRows.map((row) => (
              <TableRow key={`${row.rowIndex}-${row.accountNumber}`}>
                <TableCell>
                  <Typography variant="body2">{row.accountNumber}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.basePoints ?? '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.statusCredits ?? '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.activityTitle}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {isPending && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" mb={1}>
            Processing {progress.processed} of {progress.total}...
          </Typography>
          <LinearProgress variant="determinate" value={progressPercent} />
        </Box>
      )}

      <Stack direction="row" justifyContent="center" spacing={2}>
        <Button variant="text" onClick={onBack} disabled={isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isPending}>
          {isPending ? 'Submitting...' : 'Confirm & Submit'}
        </Button>
      </Stack>
    </Box>
  );
}
